import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import ScreenerWizard from '../components/ScreenerWizard';
import ScreenerResults from '../components/ScreenerResults';
import PriceSyncStatusBar from '../components/PriceSyncStatus';
import { API_BASE_URL } from '../services/api';

interface ScreenerCriteria {
  regions: string[];
  index_codes?: string[];
  min_price?: number | null;
  max_price?: number | null;
  min_market_cap?: number | null;
  max_market_cap?: number | null;
  min_pe_ratio?: number | null;
  max_pe_ratio?: number | null;
  min_dividend_yield?: number | null;
  max_dividend_yield?: number | null;
  min_volume?: number | null;
  sectors?: string[];
  industries?: string[];
  max_results?: number;
  // Technical analysis filters
  sma20_filter?: string | null;
  sma50_filter?: string | null;
  sma200_filter?: string | null;
  at_52w_high?: boolean;
  at_52w_low?: boolean;
  max_pct_from_52w_low?: number | null;
  max_pct_from_52w_high?: number | null;
  min_rsi?: number | null;
  max_rsi?: number | null;
  min_beta?: number | null;
  max_beta?: number | null;
  min_vol_ratio?: number | null;
  max_vol_ratio?: number | null;
  min_vol_change?: number | null;
  max_vol_change?: number | null;
  // EDGAR-derived fundamentals
  min_ev_ebitda?: number | null;
  max_ev_ebitda?: number | null;
  min_price_to_book?: number | null;
  max_price_to_book?: number | null;
  min_price_to_sales?: number | null;
  max_price_to_sales?: number | null;
  min_roe?: number | null;
  max_roe?: number | null;
  min_fcf_yield?: number | null;
  max_fcf_yield?: number | null;
  min_debt_to_equity?: number | null;
  max_debt_to_equity?: number | null;
  min_net_debt_to_ebitda?: number | null;
  max_net_debt_to_ebitda?: number | null;
  min_analyst_rating?: number | null;
  max_analyst_rating?: number | null;
}

interface StockResult {
  symbol: string;
  name: string;
  price: number;
  change: number;
  change_percent: number;
  market_cap: number;
  pe_ratio: number;
  dividend_yield: number;
  beta?: number | null;
  ev_ebitda?: number | null;
  price_to_book?: number | null;
  price_to_sales?: number | null;
  roe?: number | null;
  fcf_yield?: number | null;
  debt_to_equity?: number | null;
  net_debt_to_ebitda?: number | null;
  sector: string;
  industry: string;
  region: string;
  currency?: string;
  // Technical analysis data (present when technical filters are applied)
  rsi?: number | null;
  sma_20?: number | null;
  sma_50?: number | null;
  sma_200?: number | null;
  pct_from_sma20?: number | null;
  pct_from_sma50?: number | null;
  pct_from_sma200?: number | null;
  high_52w?: number | null;
  low_52w?: number | null;
  pct_from_52w_high?: number | null;
  pct_from_52w_low?: number | null;
  is_ath?: boolean;
  is_atl?: boolean;
}

const StockScreener: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandled = useRef(false);

  // View tab state
  type ScreenerView = 'overview' | 'fundamentals' | 'technical' | 'performance' | 'volume';
  const [viewTab, setViewTab] = useState<ScreenerView>('overview');

  // Multi-region screener state
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<StockResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [criteria, setCriteria] = useState<ScreenerCriteria>({
    regions: [],
    sectors: [],
    max_results: 100,
  });
  const [hasSearched, setHasSearched] = useState(false);

  // Peer lookup state
  const [peerTicker, setPeerTicker] = useState('');
  const [peerLoading, setPeerLoading] = useState(false);
  const [peerTarget, setPeerTarget] = useState<string | null>(null);
  const [peerInfo, setPeerInfo] = useState<{ industry: string; sector: string } | null>(null);

  // Multi-region handlers
  const handleFilterChange = (newCriteria: ScreenerCriteria) => {
    setCriteria(newCriteria);
  };

  const handleSearch = async (overrideCriteria?: ScreenerCriteria) => {
    const searchCriteria = overrideCriteria ?? criteria;
    if (overrideCriteria) setCriteria(overrideCriteria);
    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/screener/screen`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(searchCriteria),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to screen stocks');
      }

      const data = await response.json();
      setResults(data.results || []);
    } catch (err) {
      console.error('Screening error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setCriteria({
      regions: [],
      sectors: [],
      max_results: 100,
    });
    setResults([]);
    setError(null);
    setHasSearched(false);
  };

  // Peer lookup handler
  const handlePeerLookup = async () => {
    const sym = peerTicker.trim().toUpperCase();
    if (!sym) return;

    setPeerLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/screener/lookup/${encodeURIComponent(sym)}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to look up peers');
      }
      const data = await response.json();
      if (!data.results || data.results.length === 0) {
        throw new Error(`No data found for ${sym}. Make sure the ticker exists in the database.`);
      }
      setResults(data.results);
      setPeerTarget(data.target_symbol);
      setPeerInfo({ industry: data.industry, sector: data.sector });
    } catch (err) {
      console.error('Peer lookup error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      setResults([]);
      setPeerTarget(null);
      setPeerInfo(null);
    } finally {
      setPeerLoading(false);
    }
  };

  const clearPeerLookup = () => {
    setPeerTicker('');
    setPeerTarget(null);
    setPeerInfo(null);
    setResults([]);
    setHasSearched(false);
  };

  // Deep-link: auto-trigger peer lookup from ?lookup= query param
  const doPeerLookup = useCallback(async (sym: string) => {
    setPeerTicker(sym);
    setPeerLoading(true);
    setError(null);
    setHasSearched(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/screener/lookup/${encodeURIComponent(sym)}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to look up peers');
      }
      const data = await response.json();
      if (!data.results || data.results.length === 0) {
        throw new Error(`No data found for ${sym}.`);
      }
      setResults(data.results);
      setPeerTarget(data.target_symbol);
      setPeerInfo({ industry: data.industry, sector: data.sector });
    } catch (err) {
      console.error('Peer lookup error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      setResults([]);
      setPeerTarget(null);
      setPeerInfo(null);
    } finally {
      setPeerLoading(false);
    }
  }, []);

  useEffect(() => {
    const lookupParam = searchParams.get('lookup');
    if (lookupParam && !deepLinkHandled.current) {
      deepLinkHandled.current = true;
      setSearchParams({}, { replace: true });
      doPeerLookup(lookupParam.toUpperCase());
    }
  }, [searchParams, setSearchParams, doPeerLookup]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Stock Screener
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Discover investment opportunities across global markets
          </Typography>
        </Box>
      </Box>

      {/* Peer Comparison Search */}
      <Paper sx={{ mb: 2, p: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupWorkIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
              Peer Comparison
            </Typography>
            <TextField
              size="small"
              placeholder="Enter ticker (e.g. AAPL, PSN.L)"
              value={peerTicker}
              onChange={(e) => setPeerTicker(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === 'Enter') handlePeerLookup(); }}
              disabled={peerLoading}
              sx={{ flex: 1, maxWidth: 300, '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handlePeerLookup}
              disabled={peerLoading || !peerTicker.trim()}
              sx={{ fontSize: '0.7rem', py: 0.25, px: 1.5, minWidth: 0, height: 32 }}
            >
              {peerLoading ? <CircularProgress size={16} /> : 'Find Peers'}
            </Button>
            {peerTarget && (
              <>
                <Chip
                  label={`${peerTarget} — ${peerInfo?.sector} / ${peerInfo?.industry}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', maxWidth: 400 }}
                />
                <IconButton size="small" onClick={clearPeerLookup} sx={{ p: 0.25 }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </>
            )}
          </Box>
        </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <PriceSyncStatusBar />

      {/* Screener wizard — full step-by-step flow up front, no toggle to
          click through. Collapses itself to a compact summary bar once a
          screen has actually run, so results get the room. */}
      <Paper sx={{ mb: 3, p: 3 }}>
        <ScreenerWizard
          criteria={criteria}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          onLoadSavedScreen={(c) => handleSearch(c)}
          onReset={handleReset}
          loading={loading}
          hasSearched={hasSearched}
          resultCount={results.length}
        />
      </Paper>

      {/* Results — full width */}
      <Box>
        {loading && !hasSearched && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '300px',
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {!loading && !hasSearched && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '300px',
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Ready to find your next investment
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Select your criteria and click "Screen" to begin
            </Typography>
          </Box>
        )}

        {hasSearched && !loading && results.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {(['overview', 'fundamentals', 'technical', 'performance', 'volume'] as ScreenerView[]).map(v => (
              <Chip
                key={v}
                label={v.charAt(0).toUpperCase() + v.slice(1)}
                size="small"
                clickable
                onClick={() => setViewTab(v)}
                sx={{
                  fontSize: 11,
                  height: 24,
                  bgcolor: viewTab === v ? '#f59e0b' : 'transparent',
                  color: viewTab === v ? '#000' : 'text.secondary',
                  border: `1px solid ${viewTab === v ? '#f59e0b' : 'divider'}`,
                  fontWeight: viewTab === v ? 700 : 400,
                  '&:hover': { bgcolor: viewTab === v ? '#f59e0b' : 'action.hover' },
                }}
              />
            ))}
          </Box>
        )}

        {hasSearched && !loading && (
          <ScreenerResults
            results={results}
            loading={loading}
            criteria={criteria}
            targetSymbol={peerTarget}
            viewTab={viewTab}
          />
        )}
      </Box>
    </Box>
  );
};

export default StockScreener;
